import { HTTPClientInterface, HTTPRequest } from '@shared/core/HTTPClientInterface';
import { buildURL, SystemURL } from "./URLCatalog";

export type QuestionFilter = "inbox" | "read" | "responded" | "archived";

export interface QuestionRecord {
    id: string;
    organizationID: string;
    userID: string;
    collaboratorName: string;
    collaboratorImageURL?: string;
    createdAt: number;
    updatedAt?: number;
    text: string;
    read: boolean;
    responded: boolean;
    archived: boolean;
    responseText?: string;
    respondedAt?: number;
    respondedBy?: string;
}

export interface SendQuestionResponsePayload {
    text: string;
}

export class QuestionsService {
    private httpClient: HTTPClientInterface;

    constructor(httpClient: HTTPClientInterface) {
        this.httpClient = httpClient;
    }

    async getQuestionsByFilter(
        organizationID: string,
        filter: QuestionFilter,
    ): Promise<Array<QuestionRecord>> {
        const request = new HTTPRequest();
        request.url = buildURL(this.getListEndpoint(filter), {
            org_id: organizationID,
        });
        request.method = "GET";

        const response = await this.httpClient.execute(request);
        return response.result as Array<QuestionRecord>;
    }

    async getInboxQuestions(organizationID: string): Promise<Array<QuestionRecord>> {
        return this.getQuestionsByFilter(organizationID, "inbox");
    }

    async getReadQuestions(organizationID: string): Promise<Array<QuestionRecord>> {
        return this.getQuestionsByFilter(organizationID, "read");
    }

    async getRespondedQuestions(organizationID: string): Promise<Array<QuestionRecord>> {
        return this.getQuestionsByFilter(organizationID, "responded");
    }

    async getArchivedQuestions(organizationID: string): Promise<Array<QuestionRecord>> {
        return this.getQuestionsByFilter(organizationID, "archived");
    }

    async markAsRead(questionID: string): Promise<QuestionRecord> {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.Questions.postMarkAsRead, {
            question_id: questionID,
        });
        request.method = "POST";

        const response = await this.httpClient.execute(request);
        return response.result as QuestionRecord;
    }

    async archiveQuestion(questionID: string): Promise<QuestionRecord> {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.Questions.postArchive, {
            question_id: questionID,
        });
        request.method = "POST";

        const response = await this.httpClient.execute(request);
        return response.result as QuestionRecord;
    }

    async unarchiveQuestion(questionID: string): Promise<QuestionRecord> {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.Questions.postUnarchive, {
            question_id: questionID,
        });
        request.method = "POST";

        const response = await this.httpClient.execute(request);
        return response.result as QuestionRecord;
    }

    async sendResponse(
        questionID: string,
        payload: SendQuestionResponsePayload,
    ): Promise<QuestionRecord> {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.Questions.postRespond, {
            question_id: questionID,
        });
        request.method = "POST";
        request.body = {
            text: payload.text,
        };

        const response = await this.httpClient.execute(request);
        return response.result as QuestionRecord;
    }

    private getListEndpoint(filter: QuestionFilter): SystemURL.Questions {
        switch (filter) {
            case "read":
                return SystemURL.Questions.getRead;
            case "responded":
                return SystemURL.Questions.getResponded;
            case "archived":
                return SystemURL.Questions.getArchived;
            case "inbox":
            default:
                return SystemURL.Questions.getInbox;
        }
    }
}
